import React, { useState, useRef, useEffect } from 'react';
import { Menu, Trash2, ChevronDown } from 'lucide-react';
import { useHistory, useLocation } from 'react-router-dom';
import { ReactSortable } from 'react-sortablejs';
import { createPortal } from 'react-dom';
import apiHelper from '../../apiHelper.js';
import DiscardConfirmationModal from '../../components/DiscardConfirmationModal.js';
import StockSearchDropdown from '../../components/StockSearchDropdown.js';
import AlertNotification from '../../components/AlertNotification.js';
import { isPlaceholderStock } from '../../utils/placeholderStock';
import StockTags from '../../components/StockTags';
import './style.css';

function fmt2(value) {
    if (value == null || value === '' || value === '-') return '-';
    const num = Number(String(value).replace(/[^\d.-]/g, ''));
    if (isNaN(num)) return '-';
    return num.toFixed(2);
}

// ── Portal root for platform dropdowns ─────────────────────────────────────────
const portalRoot = typeof document !== 'undefined' ? document.getElementById('portal-root') : null;
const OptionPortal = ({ children, style }) => {
    const node = (
        <div style={{ position: 'absolute', background: '#fff', border: '1px solid #DEE2E6', borderRadius: 8, boxShadow: '0px 4px 12px rgba(0,0,0,0.12)', zIndex: 9999, minWidth: 180, overflow: 'auto', ...style }}>
            {children}
        </div>
    );
    return portalRoot ? createPortal(node, portalRoot) : node;
};

// ── Stepper ─────────────────────────────────────────────────────────────────────
function Stepper({ currentStep }) {
    const steps = [
        { num: '01', label: 'Listing and Mapping' },
        { num: '02', label: 'Test Sync and Edit' },
    ];
    // Step 1 → left half black, right half gray; Step 2 → full black
    const lineBackground = currentStep >= 2
        ? '#212529'
        : 'linear-gradient(to right, #212529 50%, #CED4DA 50%)';

    return (
        <div style={{ height: 125, padding: '20px 10px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
            {/* Single flex row: [circle1-wrapper] [line] [circle2-wrapper]
                Labels are absolutely positioned centered under each circle,
                so the line endpoints sit flush against the circle edges. */}
            <div style={{ display: 'flex', alignItems: 'center' }}>
                {steps.map((step, idx) => (
                    <React.Fragment key={step.num}>
                        {/* Circle wrapper — relative so label can be absolutely centered */}
                        <div style={{ position: 'relative', flexShrink: 0 }}>
                            <div style={{
                                width: 35, height: 35, borderRadius: '50%',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontFamily: 'Montserrat', fontSize: 12, fontWeight: 700,
                                background: '#FFEB00', color: '#000',
                            }}>
                                {step.num}
                            </div>
                            {/* Label — centered horizontally under circle via left:50% + translateX(-50%) */}
                            <div style={{
                                position: 'absolute', top: 43, left: '50%',
                                transform: 'translateX(-50%)',
                                fontSize: 16, fontWeight: 600, fontFamily: 'Montserrat',
                                color: '#212529', whiteSpace: 'nowrap',
                            }}>
                                {step.label}
                            </div>
                        </div>
                        {/* Connecting line — touches both circles directly */}
                        {idx < steps.length - 1 && (
                            <div style={{
                                width: 560, flexShrink: 1, minWidth: 60,
                                height: 2, background: lineBackground,
                            }} />
                        )}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
}

// ── Duplicate validation (DISABLED) ──────────────────────────────────────────────
// Duplicate platform mappings are allowed: the same platform can legitimately carry
// several rows sharing a product_id (one product, many variation SKUs) — see the
// WooCommerce case that motivated this. Kept commented out rather than deleted so the
// rule can be restored if the requirement comes back.
//
// function validateDuplicates(rows) {
//     const general = {};
//     const fields = {};
//     for (let i = 0; i < rows.length; i++) {
//         const a = rows[i];
//         if (!a.platform) continue;
//         for (let j = i + 1; j < rows.length; j++) {
//             const b = rows[j];
//             if (!b.platform || a.platform !== b.platform) continue;
//             const pidMatch = a.product_id && b.product_id && a.product_id === b.product_id;
//             const skuMatch = a.sku_id && b.sku_id && a.sku_id === b.sku_id;
//             if (pidMatch || skuMatch) {
//                 general[a.id] = general[b.id] = 'Duplicated';
//                 if (!fields[a.id]) fields[a.id] = { product_id: false, sku_id: false };
//                 if (!fields[b.id]) fields[b.id] = { product_id: false, sku_id: false };
//                 if (pidMatch) { fields[a.id].product_id = fields[b.id].product_id = true; }
//                 if (skuMatch) { fields[a.id].sku_id = fields[b.id].sku_id = true; }
//             }
//         }
//     }
//     return { general, fields };
// }

// ── Fetch product data for test sync (mirrors TestSyncModal logic) ──────────────
async function fetchProductData(row, signal) {
    try {
        if (row.platform.toLowerCase().includes('lazada')) {
            const res = await apiHelper(`lazada/get-product/?item_id=${row.product_id}`, 'GET', null, { signal });
            if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
            const data = await res.json();
            if (data.error === 'Max retries exceeded') throw new Error('Limit exceeded. Remove some Listings to continue');
            if (data.success) {
                const pd = data.data;
                const skuData = pd.skus?.find(s => s.ShopSku?.toString() === row.sku_id?.toString());
                if (!skuData) return { verified: false, product_name: pd.attributes?.name || '-', sku_name: 'No Valid SKU Found', product_price: '-', prioduct_stock: '-', hasError: true, skuError: true, errorMessage: 'SKU ID not found' };
                const skuName = skuData.saleProp ? Object.values(skuData.saleProp).join(', ') : '-';
                return { verified: true, product_name: pd.attributes?.name || '-', sku_name: skuName, product_price: skuData.price ? `SGD ${fmt2(skuData.price)}` : '-', prioduct_stock: skuData.quantity?.toString() || '0', normal_price: skuData.list_price != null ? Number(skuData.list_price) : null, hasError: false };
            }
            return { verified: false, product_name: 'No Valid Product Found', sku_name: 'No Valid SKU Found', product_price: '-', prioduct_stock: '-', hasError: true, productError: true, errorMessage: data.message || 'Product not found' };
        } else if (row.platform.toLowerCase().includes('shopee')) {
            const res = await apiHelper(`shopee/get-product-info/?item_id=${row.product_id}`, 'GET', null, { signal });
            if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
            const data = await res.json();
            if (data.status === 'success' && data.data?.valid_items?.length > 0) {
                const pd = data.data.valid_items[0];
                if (pd.has_model) {
                    const mRes = await apiHelper(`shopee/get-model-list/?item_id=${row.product_id}`, 'GET', null, { signal });
                    const mData = await mRes.json();
                    const model = mData.models?.find(m => m.model_id?.toString() === row.sku_id?.toString());
                    if (!model) return { verified: false, product_name: pd.item_name || '-', sku_name: 'No Valid SKU Found', product_price: '-', prioduct_stock: '-', hasError: true, skuError: true, errorMessage: 'Model ID not found' };
                    return { verified: true, product_name: pd.item_name || '-', sku_name: model.model_name || model.model_sku || '-', product_price: model.price_info ? `SGD ${fmt2(model.price_info[0].current_price)}` : '-', prioduct_stock: model.stock_info?.seller_stock?.[0]?.stock?.toString() || '0', hasError: false };
                }
                return { verified: true, product_name: pd.item_name || '-', sku_name: pd.item_sku || '-', product_price: pd.price_info?.current_price ? `SGD ${fmt2(pd.price_info.current_price)}` : '-', prioduct_stock: pd.stock_info?.stock?.toString() || '0', hasError: false };
            }
            return { verified: false, product_name: 'No Valid Product Found', sku_name: 'No Valid SKU Found', product_price: '-', prioduct_stock: '-', hasError: true, productError: true, errorMessage: data.message || 'Product not found' };
        } else if (row.platform.toLowerCase().includes('tiktok') || row.platform.toLowerCase().includes('tik tok')) {
            const res = await apiHelper(`tiktok/products/${row.product_id}/`, 'GET', null, { signal });
            if (!res.ok) throw new Error(`TikTok API error! status: ${res.status}`);
            const data = await res.json();
            if (data.error === true) return { verified: false, product_name: 'No Valid Product Found', sku_name: 'No Valid SKU Found', product_price: '-', prioduct_stock: '-', hasError: true, productError: true, errorMessage: data.message || 'TikTok API error' };
            const skuData = data.skus?.find(s => s.id?.toString() === row.sku_id?.toString());
            if (!skuData) return { verified: false, product_name: data.title || '-', sku_name: 'No Valid SKU Found', product_price: '-', prioduct_stock: '-', hasError: true, skuError: true, errorMessage: 'SKU ID not found' };
            const skuName = skuData.sales_attributes?.map(a => a.value_name).filter(Boolean).join(' - ') || '-';
            const price = skuData.price?.sale_price || skuData.price?.tax_exclusive_price;
            const stock = skuData.inventory?.reduce((s, i) => s + (parseInt(i.quantity) || 0), 0) || 0;
            return { verified: true, product_name: data.title || '-', sku_name: skuName, product_price: price ? `SGD ${fmt2(price)}` : '-', prioduct_stock: stock.toString(), hasError: false };
        } else if (row.platform.includes('WooCommerce') || row.platform.includes('Woocommerce')) {
            const res = await apiHelper(`woocommerce/validate-product/?product_id=${row.product_id}&variation_id=${row.sku_id}`, 'GET', null, { signal });
            if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
            const data = await res.json();
            if (data.valid) {
                return { verified: true, product_name: data.product_name || '-', sku_name: data.sku_name || '-', product_price: data.price ? `SGD ${fmt2(data.price)}` : '-', prioduct_stock: data.stock_quantity?.toString() || '0', hasError: false };
            }
            const productInvalid = data.product_valid === false || !data.product_name;
            return {
                verified: false,
                product_name: productInvalid ? 'No Valid Product Found' : (data.product_name || '-'),
                sku_name: 'No Valid SKU Found',
                product_price: '-', prioduct_stock: '-',
                hasError: true,
                productError: productInvalid ? true : undefined,
                skuError: productInvalid ? undefined : true,
                errorMessage: data.error || 'Invalid WooCommerce Product ID or Variation ID',
            };
        } else {
            // Unknown platform — skip sync, mark as pending
            return { verified: false, product_name: '-', sku_name: '-', product_price: '-', prioduct_stock: '-', hasError: false, skipped: true };
        }
    } catch (err) {
        if (err.name === 'AbortError') return { verified: false, product_name: '-', sku_name: '-', product_price: '-', prioduct_stock: '-', hasError: false, aborted: true };
        return { verified: false, product_name: 'No Valid Product Found', sku_name: 'No Valid SKU Found', product_price: '-', prioduct_stock: '-', hasError: true, productError: true, errorMessage: err.message };
    }
}

// ── Icons ────────────────────────────────────────────────────────────────────────
const SuccessIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M7.99967 1.3335C4.32367 1.3335 1.33301 4.32416 1.33301 8.00016C1.33301 11.6762 4.32367 14.6668 7.99967 14.6668C11.6757 14.6668 14.6663 11.6762 14.6663 8.00016C14.6663 4.32416 11.6757 1.3335 7.99967 1.3335ZM7.11631 10.4929C6.86809 10.7411 6.46571 10.7413 6.21722 10.4933L4.66331 8.94278C4.40281 8.68284 4.40228 8.26096 4.66213 8.00037C4.92215 7.73962 5.34435 7.73914 5.60496 7.99929L6.66567 9.05816L9.72367 6.00016C9.98398 5.73985 10.406 5.73985 10.6663 6.00016C10.9267 6.26047 10.9267 6.68252 10.6663 6.94283L7.11631 10.4929Z" fill="#146C43"/>
    </svg>
);
const FailedIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M7.96834 1.3335C4.30967 1.3335 1.33301 4.32416 1.33301 8.00016C1.33301 11.6762 4.32367 14.6668 7.99967 14.6668C11.6757 14.6668 14.6663 11.6762 14.6663 8.00016C14.6663 4.32416 11.6617 1.3335 7.96834 1.3335ZM8.66634 10.6668C8.66634 11.035 8.36786 11.3335 7.99967 11.3335C7.63148 11.3335 7.33301 11.035 7.33301 10.6668C7.33301 10.2986 7.63148 10.0002 7.99967 10.0002C8.36786 10.0002 8.66634 10.2986 8.66634 10.6668ZM8.66634 8.00016C8.66634 8.36835 8.36786 8.66683 7.99967 8.66683C7.63148 8.66683 7.33301 8.36835 7.33301 8.00016V5.3335C7.33301 4.96531 7.63148 4.66683 7.99967 4.66683C8.36786 4.66683 8.66634 4.96531 8.66634 5.3335V8.00016Z" fill="#DC3545"/>
    </svg>
);

// ── Step 1 seeding ───────────────────────────────────────────────────
// One shape feeds step 1, whether the mappings came from the Platform Listing
// row we were opened with or from the listing's own mappings endpoint.
const platformsToMappings = platforms => (platforms || []).map(p => ({
    platform: p.name || '',
    product_id: p.platformProductId || '',
    sku_id: p.platformSkuId || '',
    mapping_id: p.mappingId ?? null,
    stock_sync_option: p.stockSyncOption ?? null,
    notes: p.notes || '',
}));

// Rows plus the two per-row maps that are keyed by row id (Stock Update option
// and Remark), built together so the keys can never drift apart.
const buildStateFromMappings = (mappings, startId = 1) => {
    const rows = [], opts = {}, notes = {};
    mappings.forEach((m, i) => {
        const id = startId + i;
        rows.push({
            id,
            platform: m.platform || '',
            product_id: m.product_id || '',
            sku_id: m.sku_id || '',
            platform_open: false,
            mappingId: m.mapping_id ?? null,
        });
        const isWoo = (m.platform || '').toLowerCase().includes('woocommerce');
        if (m.stock_sync_option === 'sync_to_0') opts[id] = 'set_to_0';
        else if (m.stock_sync_option === 'sync_erp') opts[id] = 'sync_erp';
        else if (!isWoo) opts[id] = 'sync_erp'; // default for non-WooCommerce
        notes[id] = m.notes || '';
    });
    return { rows, opts, notes };
};

// ── Main Component ───────────────────────────────────────────────────────────────
export default function AddEditPlatformListing() {
    const history  = useHistory();
    const location = useLocation();

    const editListing = location.state?.listing ?? null;
    const isEdit = !!editListing;
    const fromDrawer = location.state?.fromDrawer ?? null;
    const breadcrumbs = ['Listing', 'Platform Listing', isEdit ? 'Edit Listing' : 'Add Listing'];

    // ── Step state ──────────────────────────────────────────────────────────────
    const [step, setStep] = useState(1);

    // ── Step 1 form state ───────────────────────────────────────────────────────
    const [selectedStock, setSelectedStock] = useState(
        isEdit ? {
            id: editListing.stockId,
            title: editListing.name,
            sku: editListing.variant,
            model_number: editListing.model_number || '',
            condition: editListing.condition,
            condition_type: editListing.grading,
            // ERP-5160: the table row carries the Stock's set type as setType;
            // without it the Stock box tagged every edited listing "None".
            set_type: editListing.setType || '',
            listed: false,
            status: editListing.stockStatus || 'Active',
        } : null
    );
    const [stockError, setStockError] = useState('');

    // Seeded from the row we were opened with so step 1 paints immediately. That
    // row may hold only part of the listing's mappings, so the ERP-5239 effect
    // below replaces this with the listing's full set as soon as it loads.
    const buildInitRows = () => {
        if (isEdit && editListing.platforms?.length) {
            return buildStateFromMappings(platformsToMappings(editListing.platforms)).rows;
        }
        return [{ id: 1, platform: '', product_id: '', sku_id: '', platform_open: false, mappingId: null }];
    };

    const [rows, setRows]                     = useState(buildInitRows);
    // Ids of existing mappings the user removed in step 1. The backend deletes
    // only these: a mapping merely absent from platform_mappings is left alone,
    // so a mapping that failed test sync (and is therefore not sent) survives.
    const [deletedMappingIds, setDeletedMappingIds] = useState([]);
    // Stock-level remark, held on the Listing itself.
    const [listingNotes, setListingNotes]     = useState(isEdit ? (editListing.notes || '') : '');
    // Duplicate validation disabled — see validateDuplicates above.
    // const [dupErrs, setDupErrs]               = useState({ general: {}, fields: {} });
    const [showFieldErrors, setShowFieldErrors] = useState(false);
    // Field-level touched: Set of "rowId:fieldName" strings (e.g. "3:platform", "3:product_id")
    const [touchedFields, setTouchedFields]     = useState(new Set());
    const [platformOptions, setPlatformOptions] = useState([]);
    const [dropdownPos, setDropdownPos]         = useState({});
    const triggerRefs                           = useRef({});

    // ── Step 2 state ────────────────────────────────────────────────────────────
    const [isTesting, setIsTesting]       = useState(false);
    const [testResults, setTestResults]   = useState([]); // [{...row, ...result}]
    const [stockUpdateOpts, setStockUpdateOpts] = useState(() => (
        isEdit && editListing?.platforms?.length
            ? buildStateFromMappings(platformsToMappings(editListing.platforms)).opts
            : {}
    )); // rowId → 'sync_erp' | 'set_to_0'
    // Listing-level remark, one per platform mapping row. Keyed by row id like stockUpdateOpts.
    const [mappingNotes, setMappingNotes] = useState(() => (
        isEdit && editListing?.platforms?.length
            ? buildStateFromMappings(platformsToMappings(editListing.platforms)).notes
            : {}
    )); // rowId → string
    const abortControllerRef              = useRef(null);
    // Only consulted for the placeholder stock - see runTestSync.
    const syncCacheRef                    = useRef({});
    // Smart sync cache: key = `${platform}|${product_id}|${sku_id}` → result

    // ── Dirty / discard ─────────────────────────────────────────────────────────
    const [isDirty, setIsDirty]           = useState(false);
    const [isSaving, setIsSaving]         = useState(false);
    const [isDiscarding, setIsDiscarding] = useState(false);
    const [discardOpen, setDiscardOpen]   = useState(false);
    const [nextLocation, setNextLocation] = useState(null);
    // Ref so block callback can read dirty/discarding synchronously (React state updates are async)
    const isDirtyRef      = useRef(false);
    const isDiscardingRef = useRef(false);
    const isSavingRef     = useRef(false);

    // ── Toast ────────────────────────────────────────────────────────────────────
    const [notification, setNotification] = useState({ show: false, type: 'error', message: '' });
    const fireToast = (msg, type = 'error') => setNotification({ show: true, type, message: msg });

    // ── Nav guard — registered once, reads refs synchronously so state batching doesn't bite us ──
    useEffect(() => {
        const unblock = history.block(tx => {
            if (!isDirtyRef.current || isDiscardingRef.current || isSavingRef.current) return; // allow navigation
            setNextLocation(tx);
            setDiscardOpen(true);
            return false; // block navigation
        });
        return () => unblock();
    }, [history]); // eslint-disable-line

    useEffect(() => {
        const h = e => { if (isDirty && !isDiscarding && !isSaving) { e.preventDefault(); e.returnValue = ''; } };
        window.addEventListener('beforeunload', h);
        return () => window.removeEventListener('beforeunload', h);
    }, [isDirty, isDiscarding, isSaving]);

    // Every platform listing (existing and newly added) is test-synced fresh while
    // editing, so we intentionally do NOT pre-seed the sync cache from DB data.

    // ── Fetch In Stock count for selected stock ──────────────────────────────────
    useEffect(() => {
        if (!selectedStock?.id) return;
        apiHelper(`inventory/stocks/${selectedStock.id}/branch-balance/`, 'GET')
            .then(res => res.ok ? res.json() : null)
            .then(data => {
                if (!data) return;
                const total = data.reduce((sum, b) => sum + (b.count || 0), 0);
                setSelectedStock(prev => prev ? { ...prev, inStock: total } : prev);
            })
            .catch(() => {});
    }, [selectedStock?.id]); // eslint-disable-line

    // ── Close platform dropdowns on outside click ────────────────────────────────
    useEffect(() => {
        const handler = e => {
            const inPortal = portalRoot && portalRoot.contains(e.target);
            if (inPortal) return;
            const clickedTrigger = Object.values(triggerRefs.current).some(el => el && el.contains && el.contains(e.target));
            if (!clickedTrigger) {
                setRows(prev => {
                    // Only mark the platform field touched for rows whose dropdown was open
                    const openIds = prev.filter(r => r.platform_open).map(r => r.id);
                    if (openIds.length) markFieldsTouched(openIds.map(id => `${id}:platform`));
                    return prev.map(r => ({ ...r, platform_open: false }));
                });
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // ── Fetch platforms ───────────────────────────────────────────────────────────
    useEffect(() => {
        apiHelper('sales/platforms/', 'GET').then(async res => {
            if (res.ok) { const d = await res.json(); setPlatformOptions(Array.isArray(d) ? d.map(p => p.platform_name) : []); }
        }).catch(console.error);
    }, []);

    // ── ERP-5239: load the listing's FULL mapping set ────────────────────────
    // Platform Listing filters mappings server-side, so a row opened while a
    // Platform (or Stock Sync / Consistency) filter was applied carries only the
    // mappings that matched it. Editing from there showed a partial listing: the
    // other platforms were invisible, and test sync, Stock Update and Remark all
    // acted as if they did not exist. The listing's own mappings endpoint is
    // never filtered, so read step 1 from that instead.
    // Step-1 state as of the last render, for the async handler below to read
    // without re-running (and re-fetching) every time a row changes.
    const step1Ref = useRef({ rows, step, deletedMappingIds });
    useEffect(() => { step1Ref.current = { rows, step, deletedMappingIds }; }, [rows, step, deletedMappingIds]);

    useEffect(() => {
        if (!isEdit || !editListing?.id) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await apiHelper(`sales/listings/${editListing.id}/platform-mappings/`, 'GET');
                if (!res.ok) return;
                const data = await res.json();
                if (cancelled || !data?.success || !Array.isArray(data.mappings)) return;

                const all = data.mappings.map(m => ({ ...m, mapping_id: m.mapping_id ?? m.id ?? null }));
                const { rows: current, step: currentStep, deletedMappingIds: deleted } = step1Ref.current;
                // Past step 1 already: test sync results are keyed by row id, so
                // reshuffling the rows underneath them would mislabel them.
                if (currentStep !== 1) return;

                // Untouched so far — the normal case, since this resolves within a
                // moment of mount. Rebuild step 1 outright, which also restores the
                // mappings' real sort_order.
                if (!isDirtyRef.current) {
                    const { rows: nextRows, opts, notes } = buildStateFromMappings(all);
                    if (!nextRows.length) return;
                    setRows(nextRows);
                    setStockUpdateOpts(opts);
                    setMappingNotes(notes);
                    return;
                }

                // Already edited: keep every row as the user left it — including
                // the ones they deleted — and append only the mappings they never
                // got to see.
                const seen = new Set(current.map(r => r.mappingId).filter(id => id != null));
                const missing = all.filter(m => m.mapping_id != null && !seen.has(m.mapping_id) && !deleted.includes(m.mapping_id));
                if (!missing.length) return;
                const startId = (current.length ? Math.max(...current.map(r => r.id)) : 0) + 1;
                const { rows: added, opts, notes } = buildStateFromMappings(missing, startId);
                setRows([...current, ...added]);
                setStockUpdateOpts(prev => ({ ...prev, ...opts }));
                setMappingNotes(prev => ({ ...prev, ...notes }));
            } catch (e) {
                // Leave the row-seeded mappings in place. Nothing is lost on save:
                // the backend only deletes ids listed in deleted_mapping_ids.
                console.error(e);
            }
        })();
        return () => { cancelled = true; };
    }, [isEdit, editListing?.id]); // eslint-disable-line

    // ── Row helpers ───────────────────────────────────────────────────────────────
    const markDirty = () => { isDirtyRef.current = true; setIsDirty(true); };
    // Mark a single field touched: "rowId:field"
    const markFieldTouched = (rowId, field) => setTouchedFields(prev => { const next = new Set(prev); next.add(`${rowId}:${field}`); return next; });
    // Mark a specific set of fields touched (used when outside-clicking closes the platform dropdown)
    const markFieldsTouched = (entries) => setTouchedFields(prev => { const next = new Set(prev); entries.forEach(e => next.add(e)); return next; });
    // Mark ALL 3 fields for ALL given row ids (used by Next button)
    const markAllRowFields = (rowIds) => setTouchedFields(prev => {
        const next = new Set(prev);
        rowIds.forEach(id => { next.add(`${id}:platform`); next.add(`${id}:product_id`); next.add(`${id}:sku_id`); });
        return next;
    });
    // Convenience: mark single row's specific field — aliased for readability
    const markRowTouched = (rowId) => markAllRowFields([rowId]);
    const addRow = () => {
        const newId = rows.length ? Math.max(...rows.map(r => r.id)) + 1 : 1;
        setRows(prev => [...prev, { id: newId, platform: '', product_id: '', sku_id: '', platform_open: false, mappingId: null }]);
        markDirty();
    };
    const deleteRow = id => {
        setRows(prev => {
            const removed = prev.find(r => r.id === id);
            if (removed?.mappingId != null) {
                setDeletedMappingIds(ids => ids.includes(removed.mappingId) ? ids : [...ids, removed.mappingId]);
            }
            const next = prev.filter(r => r.id !== id);
            // setDupErrs(validateDuplicates(next));
            return next;
        });
        // Remove all touched-field entries for this row so a future row re-using the same id starts clean
        setTouchedFields(prev => {
            const next = new Set(prev);
            next.delete(`${id}:platform`);
            next.delete(`${id}:product_id`);
            next.delete(`${id}:sku_id`);
            return next;
        });
        markDirty();
    };
    const updateRow = (id, ch) => { setRows(prev => { const next = prev.map(r => r.id === id ? { ...r, ...ch } : r); /* setDupErrs(validateDuplicates(next)); */ return next; }); markDirty(); };
    const openDropdown = rowId => {
        const el = triggerRefs.current[rowId];
        if (el) { const r = el.getBoundingClientRect(); setDropdownPos(prev => ({ ...prev, [rowId]: { top: r.bottom + window.scrollY + 4, left: r.left + window.scrollX, width: Math.max(280, r.width) } })); }
        setRows(prev => prev.map(r => ({ ...r, platform_open: r.id === rowId })));
    };

    // ── Stock ─────────────────────────────────────────────────────────────────────
    const handleStockSelect = opt => {
        setSelectedStock(opt);
        if (!opt) { setStockError('Stock is required'); markDirty(); return; }
        if (opt.listed && !isPlaceholderStock(opt.title, opt.sku) && !(isEdit && opt.id === editListing?.stockId)) setStockError('Stock is already listed. Select another one or go back to edit the existing Listing.');
        else if (opt.status === 'Inactive') setStockError('This stock is inactive. Please select another.');
        else setStockError('');
        markDirty();
    };

    // ── Step 1 → 2 ───────────────────────────────────────────────────────────────
    const handleNext = () => {
        setShowFieldErrors(true);
        markAllRowFields(rows.map(r => r.id));
        if (!selectedStock) { setStockError('Stock is required'); fireToast('Please fix the errors below'); return; }
        if (stockError) { fireToast('Please fix the errors below'); return; }
        if (rows.some(r => !r.platform || !r.product_id || !r.sku_id)) { fireToast('Please fix the errors below'); return; }
        // Duplicate mappings no longer block Next.
        // if (Object.keys(dupErrs.general).length > 0) { fireToast('Please fix the errors below'); return; }
        runTestSync();
    };

    // ── Run test sync ─────────────────────────────────────────────────────────────
    const runTestSync = async () => {
        setStep(2);

        // Every row is tested against the platform on every Next.
        //
        // It used to reuse a cached result whenever a row's
        // platform|product_id|sku_id had not changed, and to skip the step
        // entirely when no row had changed at all. That reported stale platform
        // data: price, stock and product name all move on the platform while
        // those three identifiers stay exactly the same, which is the normal
        // case for an edit. Re-testing is the point of the step.
        //
        // The placeholder stock ("No Connected Model" / "No Connected SKU") is
        // the exception. It is a catch-all - one listing carries a dozen or more
        // unrelated platform products - so re-testing all of them on every Next
        // costs far more than it tells anyone. That listing keeps the cache.
        const isPlaceholder = isPlaceholderStock(selectedStock?.title, selectedStock?.sku);

        setIsTesting(true);
        setTestResults([]);

        abortControllerRef.current = new AbortController();
        const { signal } = abortControllerRef.current;

        // Init per-row update options, skipping WooCommerce. The `!next[r.id]`
        // guard keeps a choice the user already made on this row - re-testing
        // refreshes platform data, it should not reset their stock option.
        setStockUpdateOpts(prev => {
            const next = { ...prev };
            rows.forEach(r => {
                const isWoo = r.platform?.toLowerCase().includes('woocommerce');
                if (!isWoo && !next[r.id]) next[r.id] = 'sync_erp';
            });
            return next;
        });

        const results = await Promise.all(
            rows.map(async row => {
                const cacheKey = `${row.platform}|${row.product_id}|${row.sku_id}`;
                if (isPlaceholder && syncCacheRef.current[cacheKey]) {
                    return { ...row, ...syncCacheRef.current[cacheKey] };
                }
                const result = await fetchProductData(row, signal);
                if (isPlaceholder && !result.aborted) {
                    syncCacheRef.current[cacheKey] = result;
                }
                return { ...row, ...result };
            })
        );

        setTestResults(results);
        setIsTesting(false);
    };

    // ── Save ──────────────────────────────────────────────────────────────────────
    const hasSaveableMapping = (testResults.length ? testResults : rows)
        .some(r => r.platform && r.product_id && r.sku_id && !r.hasError);

    const handleSave = async () => {
        isSavingRef.current = true;
        setIsSaving(true);
        try {
            const platformMappings = (testResults.length ? testResults : rows)
                .filter(r => r.platform && r.product_id && r.sku_id && !r.hasError)
                .map(r => {
                    const isWoo = r.platform?.toLowerCase().includes('woocommerce');
                    const opt = isWoo ? null : (stockUpdateOpts[r.id] || 'sync_erp');
                    let platformStock = 0;
                    if (isWoo) {
                        if (r.prioduct_stock && r.prioduct_stock !== '-') platformStock = parseInt(r.prioduct_stock) || 0;
                    } else {
                        if (opt === 'set_to_0') platformStock = 0;
                        else if (r.prioduct_stock && r.prioduct_stock !== '-') platformStock = parseInt(r.prioduct_stock) || 0;
                    }

                    let platformPrice = null;
                    if (r.product_price && r.product_price !== '-') {
                        const n = parseFloat(r.product_price.replace(/[^\d.-]/g, ''));
                        if (!isNaN(n)) platformPrice = n;
                    }

                    return {
                        platform: r.platform, product_id: r.product_id.trim(), sku_id: r.sku_id.trim(),
                        mapping_id: r.mappingId ?? null,
                        platform_product_name: r.product_name || '', platform_sku_name: r.sku_name || '',
                        platform_price: platformPrice, platform_stock_quantity: platformStock,
                        normal_price: r.normal_price ?? null,
                        test_status: r.verified ? 'success' : 'pending',
                        was_tested: !!(r.verified !== undefined),
                        update_stock_option: opt,
                        stock_sync_option: isWoo ? null : (opt === 'set_to_0' ? 'sync_to_0' : 'sync_erp'),
                        notes: (mappingNotes[r.id] || '').trim(),
                    };
                });

            const payload = { stock_id: selectedStock.id, listing_code: null, status: true, notes: listingNotes.trim(), platform_mappings: platformMappings };
            if (isEdit) payload.deleted_mapping_ids = deletedMappingIds;
            const endpoint = isEdit ? `sales/listings/${editListing.id}/update-with-mappings/` : 'sales/listings/create-with-mappings/';
            const res = await apiHelper(endpoint, isEdit ? 'PUT' : 'POST', payload);
            const result = await res.json();

            if (!res.ok) { setIsSaving(false); return; }

            isDirtyRef.current = false; isSavingRef.current = false;
            setIsDirty(false);
            history.push('/listing-pricing', { success: 'Listing Saved Successfully' });
        } catch (e) {
            console.error(e); setIsSaving(false);
        }
    };

    // ── Discard ───────────────────────────────────────────────────────────────────
    const handleDiscard = () => { if (isDirtyRef.current) setDiscardOpen(true); else history.push('/listing-pricing', fromDrawer ? { restoreDrawer: fromDrawer } : {}); };
    const handleBack    = () => { if (abortControllerRef.current) abortControllerRef.current.abort(); setStep(1); setIsTesting(false); setTestResults([]); };
    const confirmDiscard = () => {
        if (abortControllerRef.current) abortControllerRef.current.abort();
        // Set refs synchronously BEFORE history.push so block callback sees correct values
        isDirtyRef.current = false;
        isDiscardingRef.current = true;
        setIsDiscarding(true); setIsDirty(false); setDiscardOpen(false);
        if (nextLocation) {
            // Nav guard intercepted a specific navigation — preserve its intended state (e.g. restoreDrawer)
            history.push(nextLocation.pathname, nextLocation.state || {});
        } else {
            // Discard button clicked directly — go back to listing, restore drawer if came from one
            history.push('/listing-pricing', fromDrawer ? { restoreDrawer: fromDrawer } : { success: 'Listing Discarded', type: 'warning' });
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────────
    return (
        <div className="company-body new-look stock-wrap">
            <style>{`
                .aep-input { width:100%; border:1px solid #6C757D; border-radius:8px; padding:8px 12px; font-size:13px; font-family:Montserrat; color:#212529; outline:none; box-sizing:border-box; background:#fff; }
                .aep-input:focus { border-color:#86B7FE; box-shadow:0 0 0 3px rgba(13,110,253,.15); }
                .aep-section { background:#fff; border-radius:12px; box-shadow:0 1px 4px rgba(0,0,0,.08); margin-bottom:16px; overflow:visible; }
.aep-opt { padding:9px 14px; font-size:13px; font-family:Montserrat; cursor:pointer; color:#212529; }
                .aep-opt:hover { background:#F8F9FA; }
                .aep-err { font-size:11px; color:#DC3545; margin-top:3px; padding-left:4px; }
                .aep-del { background:none; border:none; cursor:pointer; padding:4px; color:#ADB5BD; border-radius:4px; display:flex; align-items:center; }
                .aep-del:hover:not(:disabled) { color:#DC3545; background:#FEF2F2; }
                .aep-del:disabled { opacity:.3; cursor:not-allowed; }
                .drag-handle { cursor:grab; }
                /* Step 2 */
                .aep-notify { background:#FFFEF0; border:1px solid #FFE066; border-radius:10px; padding:14px 16px; margin-bottom:16px; }
                .aep-notify ul { margin:6px 0 0 0; padding-left:18px; }
                .aep-notify li { font-size:12px; color:#212529; font-family:Montserrat; margin-bottom:4px; line-height:1.5; }
                .aep-warn-box { background:#FFFEF0; border:1px solid #FFE066; border-radius:10px; padding:14px 16px; margin-bottom:16px; display:flex; gap:10px; align-items:flex-start; }
                .aep-results-table { width:100%; border-collapse:collapse; font-family:Montserrat; font-size:13px; }
                .aep-results-table th { background:#F8F9FA; font-size:12px; font-weight:600; color:#6C757D; padding:10px 12px; text-align:left; border-bottom:1px solid #DEE2E6; }
                .aep-results-table td { padding:10px 12px; border-bottom:1px solid #F0F0F0; vertical-align:middle; }
                .aep-radio-group { display:flex; flex-direction:row; align-items:center; gap:16px; }
                .aep-radio-label { display:flex; align-items:center; gap:6px; font-size:12px; font-family:Montserrat; cursor:pointer; color:#212529; }
                .aep-radio-label-disabled { display:flex; align-items:center; gap:6px; font-size:12px; font-family:Montserrat; font-weight:600; cursor:not-allowed; color:#000; }
                .aep-radio-label input[type="radio"] { display:none; }
                .aep-radio-custom { width:20px; height:20px; border-radius:99px; border:1.5px solid #212529; background:#fff; box-shadow:0 1px 4px 0 rgba(0,0,0,0.10); flex-shrink:0; }
                .aep-radio-custom-disabled { width:20px; height:20px; border-radius:99px; border:1px solid #ADB5BD; background:#E9ECEF; box-shadow:0 1px 4px 0 rgba(0,0,0,0.10); flex-shrink:0; }
                @keyframes spin { to { transform: rotate(360deg); } }
                .aep-spinner { animation: spin 1s linear infinite; }
            `}</style>

            {/* Toast */}
            {notification.show && (
                <AlertNotification
                    type={notification.type}
                    action="create"
                    recordName="Listing"
                    message={notification.message}
                    onClose={() => setNotification(prev => ({ ...prev, show: false }))}
                />
            )}

            {/* Breadcrumb */}
            <div className="breadcrumb">
                {breadcrumbs.map((b, i) => (
                    <span
                        key={i}
                        className={`breadcrumb-item${b === 'Platform Listing' ? ' clickable' : ''}`}
                        onClick={() => { if (b === 'Platform Listing') history.push('/listing-pricing', fromDrawer ? { restoreDrawer: fromDrawer } : {}); }}
                        style={b === 'Platform Listing' ? { cursor: 'pointer' } : {}}
                    >
                        {b}{i < breadcrumbs.length - 1}
                    </span>
                ))}
            </div>

            {/* Stepper */}
            <div className="aep-section"><Stepper currentStep={step} /></div>

            {/* ════════ STEP 1 ════════ */}
            {step === 1 && (
                <div className="aep-section" style={{background:'none'}}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'20px', borderBottom:'1px solid #F0F0F0',background:'white',borderRadius:12 }}>
                        <span style={{ fontFamily:'Montserrat', fontSize:20, fontWeight:700, color:'#212529' }}>Listing and Mapping</span>
                        <div style={{ display:'flex', gap:10 }}>
                            <button onClick={handleDiscard} style={{ background:'#fff', border:'1px solid #212529', borderRadius:8, padding:'10px', fontFamily:'Montserrat', fontSize:12, fontWeight:600, cursor:'pointer', color:'#212529',minHeight:34,minWidth:100 }}>Discard</button>
                            <button onClick={handleNext} style={{ background:'#212529', border:'none', borderRadius:8, padding:'10px', fontFamily:'Montserrat', fontSize:12, fontWeight:600, cursor:'pointer', color:'#fff',minHeight:34,minWidth:100 }}>Next</button>
                        </div>
                    </div>
                    <div style={{ }}>
                        {/* Select Stock */}
                        <div style={{ border:'1px solid #F0F0F0',  margin:'12px 0', background:'white',borderRadius:12 }}>
                            <div style={{ fontFamily:'Montserrat', padding:'15px 20px',background: '#F8F9FA',borderRadius: '12px 12px 0 0',fontSize:14, fontWeight:600, color:'#212529' }}>Select Stock</div>
                            <div style={{padding:20}}>
                                <div style={{ fontFamily:'Montserrat', fontSize:12, fontWeight:700, color:'#212529', marginBottom:10 }}>Stock <span style={{ color:'#DC3545' }}>*</span></div>
                                <StockSearchDropdown
                                    selected={selectedStock}
                                    onSelect={handleStockSelect}
                                    onClear={() => { setSelectedStock(null); setStockError('Stock is required'); setIsDirty(true); }}
                                    onClose={() => { if (!selectedStock) setStockError('Stock is required'); }}
                                    error={!!stockError || (showFieldErrors && !selectedStock)}
                                />
                                {!selectedStock && (stockError || showFieldErrors) && <div className="aep-err">Stock is required</div>}
                                {selectedStock && stockError && <div className="aep-err" style={{ display:'flex', alignItems:'center', gap:4 }}>{stockError}</div>}
                            </div>
                        </div>

                        {/* Platform Mapping */}
                        <div style={{ border:'1px solid #F0F0F0', borderRadius:10 ,background:'white',borderRadius:12,  margin:'12px 0'}}>
                            <div style={{ fontFamily:'Montserrat', fontSize:14, fontWeight:600, color:'#212529',  padding:'15px 20px',background:'#F8F9FA',borderRadius:'12px 12px 0 0' }}>Platform Mapping</div>
                            <div style={{padding:20}}>
                                <div style={{ fontSize:14,fontWeight:400, color:'#212529', fontFamily:'Montserrat', marginBottom:20 }}>Cannot map: draft, unapproved or deleted Products and SKUs from platforms.</div>
                                <div className="info-content d-block entities-tab" style={{padding:0,border:'none'}}>
                                <div style={{ borderRadius: '12px',border:'none' }}>
                                <div className="table-wrapper" style={{ position: 'relative', overflowX: 'auto',border:'none' }}>
                                    <table className="entities-table tab-table w-100">
                                        <thead height="35px">
                                            <tr>
                                                <th />
                                                <th><span>Platform <span style={{ color: '#DC3545' }}>*</span></span></th>
                                                <th><span>Product ID <span style={{ color: '#DC3545' }}>*</span></span></th>
                                                <th><span>SKU ID <span style={{ color: '#DC3545' }}>*</span></span></th>
                                                <th />
                                            </tr>
                                        </thead>
                                        <ReactSortable list={rows} setList={next => { setRows(next); /* setDupErrs(validateDuplicates(next)); */ setIsDirty(true); }} handle=".drag-handle" animation={150} tag="tbody">
                                            {rows.map((row, idx) => {
                                                const isFirstRow = idx === 0;
                                                // const dupField = dupErrs.fields[row.id];
                                                const errPlat = touchedFields.has(`${row.id}:platform`)   && !row.platform;
                                                const errProd = touchedFields.has(`${row.id}:product_id`) && !row.product_id;
                                                const errSku  = touchedFields.has(`${row.id}:sku_id`)     && !row.sku_id;
                                                const pos = dropdownPos[row.id];
                                                return (
                                                    <tr key={row.id} style={{ position: row.platform_open ? 'relative' : 'static', zIndex: row.platform_open ? 1000 : 'auto' }}>
                                                        {/* Delete */}
                                                        <td style={{ width: '60px', verticalAlign: 'middle', padding: '15px 20px' }}>
                                                            <button className="btn p-0" disabled={isFirstRow}
                                                                style={{ boxShadow: 'none', opacity: isFirstRow ? 0.3 : 1, cursor: isFirstRow ? 'not-allowed' : 'pointer', position: 'relative', top: '-2px' }}
                                                                onClick={e => { e.preventDefault(); e.stopPropagation(); if (!isFirstRow) deleteRow(row.id); }}
                                                                title={isFirstRow ? 'First row cannot be deleted' : 'Delete row'}>
                                                                <Trash2 size={18} />
                                                            </button>
                                                        </td>

                                                        {/* Platform dropdown */}
                                                        <td style={{ position: 'relative', minWidth: '200px', maxWidth: '200px', verticalAlign: 'top', padding: '15px 20px' }}>
                                                            <div ref={el => { triggerRefs.current[row.id] = el; }}
                                                                className="form-control new-form-select-cls"
                                                                onClick={() => openDropdown(row.id)}
                                                                /* Duplicate highlight removed — was: borderColor/marginBottom keyed off (errPlat || dupErrs.general[row.id]) */
                                                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem', minHeight: '40px', background: 'none', borderColor: errPlat ? '#DC3545' : '#6C757D', marginBottom: errPlat ? '2px' : '0' }}>
                                                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: 'auto',color:'#212529' }}>
                                                                    {row.platform || ''}
                                                                </span>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                    {row.platform && (
                                                                        <span
                                                                            onMouseDown={e => { e.preventDefault(); e.stopPropagation(); markFieldTouched(row.id, 'platform'); updateRow(row.id, { platform: '', platform_open: false }); }}
                                                                            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                                                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                                                <path d="M9 3L3 9M3 3L9 9" stroke="#212529" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                                                            </svg>
                                                                        </span>
                                                                    )}
                                                                    <ChevronDown size={16} style={{ transform: row.platform_open ? 'rotate(180deg)' : 'rotate(0)' }} />
                                                                </div>
                                                            </div>
                                                            {errPlat && <div style={{ color: '#DC3545', fontSize: '11px', lineHeight: '14px', marginTop: '2px', paddingLeft: '4px' }}>Platform is required</div>}
                                                            {/* {dupErrs.general[row.id] && !errPlat && <div style={{ color: '#DC3545', fontSize: '11px', lineHeight: '14px', marginTop: '2px', paddingLeft: '4px' }}>Duplicated</div>} */}

                                                            {row.platform_open && pos && (
                                                                <OptionPortal style={{ top: pos.top, left: pos.left, width: pos.width }}>
                                                                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                                                        {platformOptions.map((opt, i) => (
                                                                            <li key={i}
                                                                                onMouseDown={e => { e.preventDefault(); markFieldTouched(row.id, 'platform'); updateRow(row.id, { platform: opt, platform_open: false }); }}
                                                                                onMouseEnter={e => e.currentTarget.style.background = '#FFFEF0'}
                                                                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                                                                style={{ padding: '10px', cursor: 'pointer', borderRadius: 6, fontFamily: 'Montserrat', color: '#212529', backgroundColor: 'transparent' }}>
                                                                                {opt}
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </OptionPortal>
                                                            )}
                                                        </td>

                                                        {/* Product ID */}
                                                        <td style={{ minWidth: '200px', maxWidth: '200px', position: 'relative', verticalAlign: 'top', padding: '15px 20px' }}>
                                                            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                                                <input id={`product_id_${row.id}`} name={`product_id_${row.id}`}
                                                                    className="form-control common-input-all"
                                                                    value={row.product_id}
                                                                    onChange={e => updateRow(row.id, { product_id: e.target.value })}
                                                                    onBlur={() => { markFieldTouched(row.id, 'product_id'); if (row.product_id !== row.product_id.trim()) updateRow(row.id, { product_id: row.product_id.trim() }); }}
                                                                    /* was: border keyed off (dupField?.product_id || errProd) */
                                                                    style={{ border: `1px solid ${errProd ? '#DC3545' : '#6C757D'}`,color:'#212529', paddingRight: row.product_id ? '28px' : '12px' }} />
                                                                {row.product_id && (
                                                                    <span
                                                                        onClick={e => { e.stopPropagation(); markFieldTouched(row.id, 'product_id'); updateRow(row.id, { product_id: '' }); }}
                                                                        style={{ position: 'absolute', right: '8px', display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                                            <path d="M9 3L3 9M3 3L9 9" stroke="#212529" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                                                        </svg>
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {/* {dupField?.product_id && <div style={{ color: '#DC3545', fontSize: '11px', lineHeight: '14px', marginTop: '2px', paddingLeft: '4px' }}>Duplicated</div>} */}
                                                            {errProd && <div style={{ color: '#DC3545', fontSize: '11px', lineHeight: '14px', marginTop: '2px', paddingLeft: '4px' }}>Product ID is required</div>}
                                                        </td>

                                                        {/* SKU ID */}
                                                        <td style={{ minWidth: '200px', maxWidth: '200px', position: 'relative', verticalAlign: 'top', padding: '15px 20px' }}>
                                                            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                                                <input id={`sku_id_${row.id}`} name={`sku_id_${row.id}`}
                                                                    className="form-control common-input-all"
                                                                    value={row.sku_id}
                                                                    onChange={e => updateRow(row.id, { sku_id: e.target.value })}
                                                                    onBlur={() => { markFieldTouched(row.id, 'sku_id'); if (row.sku_id !== row.sku_id.trim()) updateRow(row.id, { sku_id: row.sku_id.trim() }); }}
                                                                    /* was: border keyed off (dupField?.sku_id || errSku) */
                                                                    style={{ border: `1px solid ${errSku ? '#DC3545' : '#6C757D'}`,color:'#212529', paddingRight: row.sku_id ? '28px' : '12px' }} />
                                                                {row.sku_id && (
                                                                    <span
                                                                        onClick={e => { e.stopPropagation(); markFieldTouched(row.id, 'sku_id'); updateRow(row.id, { sku_id: '' }); }}
                                                                        style={{ position: 'absolute', right: '8px', display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                                            <path d="M9 3L3 9M3 3L9 9" stroke="#212529" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                                                        </svg>
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {/* {dupField?.sku_id && <div style={{ color: '#DC3545', fontSize: '11px', lineHeight: '14px', marginTop: '2px', paddingLeft: '4px' }}>Duplicated</div>} */}
                                                            {errSku && <div style={{ color: '#DC3545', fontSize: '11px', lineHeight: '14px', marginTop: '2px', paddingLeft: '4px' }}>SKU ID is required</div>}
                                                        </td>

                                                        {/* Drag handle */}
                                                        <td className="drag-handle" style={{ cursor: 'grab', verticalAlign: 'middle', padding: '15px 20px' }}>
                                                            <Menu size={18} />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </ReactSortable>
                                    </table>
                                </div>

                                    <div className="info-section" style={{ marginTop: '20px' }}>
                                        <div className="info-card" style={{ padding: '10px', boxShadow: 'none', overflow: 'visible' }}>
                                            <button onClick={addRow} className="dark-button" style={{
                                                display: 'flex',
                                                height: '34px',
                                                minWidth: '100px',
                                                padding: '10px',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                gap: '10px',borderRadius: '8px',
                                                border: '1px solid  #212529',
                                                background: '#FFF',
                                                fontSize: '12px',
    fontWeight: 600,
    lineHeight: 'normal'}}>
                                                Add Mapping</button>
                                        </div>
                                    </div>
                                </div>{/* inner padding */}
                                </div>{/* entities-tab */}
                            </div>
                        </div>
                    </div>

                    {/*br Notes — stock-level remark. Optional, non-unique, no length cap. */}
                    <div style={{ border:'1px solid #F0F0F0', borderRadius:10 ,background:'white',borderRadius:12 }}>
                            <div style={{ fontFamily:'Montserrat', fontSize:14, fontWeight:600, color:'#212529',  padding:'15px 20px',background:'#F8F9FA',borderRadius:'12px 12px 0 0' }}>Notes</div>
                        <div className="info-content" style={{padding:20}}>
                            <div className="info-inner common-textarea">
                                <textarea
                                    className="form-control w-100 common-textarea-all"
                                    rows="4"
                                    name="listing_notes"
                                    value={listingNotes}
                                    onChange={e => { setListingNotes(e.target.value); setIsDirty(true); }}
                                    style={{ border: '1px solid #6C757D' }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ════════ STEP 2 ════════ */}
            {step === 2 && (
                <div className="aep-section" style={{background:'none'}}>
                    {/* Header */}
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'15px 20px', borderBottom:'1px solid #F0F0F0',background:'white',borderRadius:12 }}>
                        <span style={{ fontFamily:'Montserrat', fontSize:20, fontWeight:700, color:'#212529' }}>Test Sync and Edit</span>
                        <div style={{ display:'flex', gap:10 }}>
                            <button onClick={handleDiscard}disabled={isTesting} style={{ background:isTesting?'E9ECEF':'#fff', border:isTesting?'1px solid #CED4DA':'1px solid #212529', borderRadius:8, padding:'10px', fontFamily:'Montserrat', fontSize:12, fontWeight:600, cursor:'pointer', color:isTesting?'#6C757D':'#212529',minHeight:34,minWidth:100 }}>Discard</button>
                            <button onClick={handleBack} disabled={isTesting} style={{ background:isTesting?'E9ECEF':'#fff', border:isTesting?'1px solid #CED4DA':'1px solid #212529', borderRadius:8, padding:'10px', fontFamily:'Montserrat', fontSize:12, fontWeight:600, cursor: isTesting ? 'not-allowed' : 'pointer', color:isTesting?'#6C757D':'#212529',minHeight:34,minWidth:100 }}>Back</button>
                            <button onClick={handleSave} disabled={isSaving || isTesting || !hasSaveableMapping} title={!hasSaveableMapping ? 'All mappings failed test sync — nothing to save' : undefined} style={{ background:(isTesting || !hasSaveableMapping)?'#E9ECEF':'#212529', border:(isTesting || !hasSaveableMapping)?'1px solid #CED4DA':'none', borderRadius:8, padding:'10px', fontFamily:'Montserrat', fontSize:12, fontWeight:600, cursor:(isTesting || !hasSaveableMapping)?'not-allowed':'pointer', color:(isTesting || !hasSaveableMapping)?'#6C757D':'#fff', minHeight:34,minWidth:100 }}>
                                {isSaving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>

                    <div >
                        {/* ── Testing state ── */}
                        {isTesting && (
                            <div style={{background:'white',borderRadius:12,marginTop:12,padding:20}}>
                                <div className="aep-warn-box" style={{background:'#FFFEF0' }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
  <path d="M11.9999 9.00001V13M11.9999 17H12.0099M21.7299 18L13.7299 4.00001C13.5555 3.69222 13.3025 3.4362 12.9969 3.25808C12.6912 3.07996 12.3437 2.98611 11.9899 2.98611C11.6361 2.98611 11.2887 3.07996 10.983 3.25808C10.6773 3.4362 10.4244 3.69222 10.2499 4.00001L2.24993 18C2.07361 18.3054 1.98116 18.6519 1.98194 19.0045C1.98272 19.3571 2.07671 19.7033 2.25438 20.0078C2.43204 20.3124 2.68708 20.5646 2.99362 20.7388C3.30017 20.9131 3.64734 21.0032 3.99993 21H19.9999C20.3508 20.9997 20.6955 20.907 20.9992 20.7313C21.303 20.5556 21.5551 20.3031 21.7304 19.9991C21.9057 19.6951 21.998 19.3504 21.9979 18.9995C21.9978 18.6486 21.9054 18.3039 21.7299 18Z" stroke="#FFC107" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
                                    <div>
                                        <div style={{ fontFamily:'Montserrat', fontSize:16, fontWeight:700, color:'#212529', marginBottom:4 }}>Do not close or leave this page</div>
                                        <div style={{ fontFamily:'Montserrat', fontSize:14, color:'#212529',fontWeight:400 }}>Your data is being tested. Navigating away or closing this page will interrupt the process and you will need to re-test your data.</div>
                                    </div>
                                </div>
                                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'48px 20px' }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="88" height="88" viewBox="0 0 88 88" fill="none" className="aep-spinner">
                                        <circle cx="44" cy="44" r="41" stroke="#DEE2E6" strokeWidth="6"/>
                                        <mask id="path-2-inside-1_11741_88577" fill="white">
                                            <path d="M44 86C44 87.1046 44.8959 88.0047 45.9994 87.9546C51.7537 87.6928 57.4076 86.3033 62.6374 83.8578C68.4719 81.1297 73.6362 77.1539 77.7658 72.2112C81.8954 67.2685 84.8895 61.4795 86.5367 55.2529C88.0132 49.6716 88.3753 43.8608 87.6096 38.1516C87.4628 37.0568 86.4176 36.3352 85.3307 36.5316L83.6186 36.841C82.5317 37.0374 81.8152 38.0774 81.9546 39.1732C82.5753 44.0544 82.2495 49.0161 80.9878 53.785C79.5555 59.1993 76.952 64.2332 73.3611 68.5311C69.7702 72.829 65.2795 76.2861 60.2062 78.6584C55.7376 80.7479 50.9129 81.9509 45.9991 82.208C44.8961 82.2657 44 83.1557 44 84.2603V86Z"/>
                                        </mask>
                                        <path d="M44 86C44 87.1046 44.8959 88.0047 45.9994 87.9546C51.7537 87.6928 57.4076 86.3033 62.6374 83.8578C68.4719 81.1297 73.6362 77.1539 77.7658 72.2112C81.8954 67.2685 84.8895 61.4795 86.5367 55.2529C88.0132 49.6716 88.3753 43.8608 87.6096 38.1516C87.4628 37.0568 86.4176 36.3352 85.3307 36.5316L83.6186 36.841C82.5317 37.0374 81.8152 38.0774 81.9546 39.1732C82.5753 44.0544 82.2495 49.0161 80.9878 53.785C79.5555 59.1993 76.952 64.2332 73.3611 68.5311C69.7702 72.829 65.2795 76.2861 60.2062 78.6584C55.7376 80.7479 50.9129 81.9509 45.9991 82.208C44.8961 82.2657 44 83.1557 44 84.2603V86Z" stroke="#FFEB00" strokeWidth="16" strokeMiterlimit="2.9238" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="14 1" mask="url(#path-2-inside-1_11741_88577)"/>
                                    </svg>
                                    <div style={{ fontFamily:'Montserrat', fontSize:20, fontWeight:700, color:'#212529', marginTop:20 }}>Testing Synchronization...</div>
                                    <div style={{ fontFamily:'Montserrat', fontSize:14, color:'#6C757D', marginTop:6,fontWeight:400 }}>This usually takes a few seconds.</div>
                                </div>
                            </div>
                        )}

                        {/* ── Results state ── */}
                        {!isTesting && testResults.length > 0 && (
                            <>
                                {/* Notification */}
                                <div style={{ background:'#fff', border:'1px solid #DEE2E6', borderRadius:10,marginTop:12, marginBottom:16 }}>
                                    <div style={{ display:'flex', alignItems:'center', gap:8, fontFamily:'Montserrat', fontSize:12, fontWeight:600, color:'#212529', padding:'15px 20px',background:'#F8F9FA',borderRadius:'12px 12px 0px 0px'}}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                        <path d="M4.5 6C4.5 4.80653 4.97411 3.66193 5.81802 2.81802C6.66193 1.97411 7.80653 1.5 9 1.5C10.1935 1.5 11.3381 1.97411 12.182 2.81802C13.0259 3.66193 13.5 4.80653 13.5 6C13.5 11.25 15.75 12.75 15.75 12.75H2.25C2.25 12.75 4.5 11.25 4.5 6Z" fill="#FFEB00"/>
                                        <path d="M7.725 15.75C7.85054 15.9783 8.03509 16.1688 8.25937 16.3014C8.48365 16.434 8.73943 16.504 9 16.504C9.26057 16.504 9.51635 16.434 9.74063 16.3014C9.96491 16.1688 10.1495 15.9783 10.275 15.75" fill="#FFEB00"/>
                                        <path d="M7.725 15.75C7.85054 15.9783 8.03509 16.1688 8.25937 16.3014C8.48365 16.434 8.73943 16.504 9 16.504C9.26057 16.504 9.51635 16.434 9.74063 16.3014C9.96491 16.1688 10.1495 15.9783 10.275 15.75M4.5 6C4.5 4.80653 4.97411 3.66193 5.81802 2.81802C6.66193 1.97411 7.80653 1.5 9 1.5C10.1935 1.5 11.3381 1.97411 12.182 2.81802C13.0259 3.66193 13.5 4.80653 13.5 6C13.5 11.25 15.75 12.75 15.75 12.75H2.25C2.25 12.75 4.5 11.25 4.5 6Z" stroke="#212529" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                        </svg> Notification
                                    </div>
                                    <div style={{padding:20}}>
                                        <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:10 }}>
                                            <div style={{ display:'flex', gap:10, fontFamily:'Montserrat', fontSize:14, color:'#212529' }}>
                                                <span style={{ fontWeight:600, minWidth:20, flexShrink:0,color:'#ADB5BD' }}>01</span>
                                                <span>Mappings failed to sync will be skipped automatically upon saving.</span>
                                            </div>
                                            <div style={{ display:'flex', gap:10, fontFamily:'Montserrat', fontSize:14, color:'#212529' }}>
                                                <span style={{ fontWeight:600, minWidth:20, flexShrink:0,color:'#ADB5BD' }}>02</span>
                                                <span>Please update Platform Stock based on ERP In-Stock quantity.</span>
                                            </div>
                                        </div>
                                        <div style={{ display:'flex', flexDirection:'column', gap:6, paddingLeft:4 }}>
                                            <div style={{ display:'flex', gap:8, fontFamily:'Montserrat', fontSize:14, color:'#212529' }}>
                                                <span style={{ flexShrink:0 }}>•</span>
                                                <span><strong>Sync ERP Stock:</strong> Upon saving, Platform Stock will be immediately synced with ERP Stock via API.</span>
                                            </div>
                                            <div style={{ display:'flex', gap:8, fontFamily:'Montserrat', fontSize:14, color:'#212529' }}>
                                                <span style={{ flexShrink:0 }}>•</span>
                                                <span><strong>Set to 0:</strong> Upon saving, Platform Stock will be set to 0 via API immediately. You may restore it to align with ERP Stock later.</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Stock info header */}
                                <div style={{background:'white',borderRadius:12}}>
                                {selectedStock && (
                                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12,background:'white',padding:20,borderRadius:'12px 12px 0 0',backgroundColor:'#F8F9FA' }}>
                                        <div>
                                            <div style={{ fontFamily:'Montserrat', fontSize:14, fontWeight:600, color:'#212529' }}>{selectedStock.title}</div>
                                            <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:5 }}>
                                                <span style={{ fontSize:12, color:'#6C757D', fontFamily:'Montserrat' }}>{selectedStock.sku}{selectedStock.model_number ? ` | ${selectedStock.model_number}` : ''} | </span>
                                                {/* ERP-5160: Condition / Condition Type / Set Type, same chip row as the Stock box in step 1 */}
                                                <StockTags
                                                    condition={selectedStock.condition}
                                                    conditionType={selectedStock.condition_type}
                                                    setType={selectedStock.set_type}
                                                    style={{ minWidth:'unset', padding:'2px 5px', fontSize:'8px', lineHeight:'normal', background:'#FFEB00',borderRadius:99,color:'#212529',fontWeight:600 }}
                                                />
                                                <span style={{ fontSize:12, color:'#6C757D', fontFamily:'Montserrat' }}>| In Stock: {selectedStock.inStock ?? '-'}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Results table */}
                                <div style={{padding:20}}>
                                    <div className="new-look"><div className="entities-tab test-sync" style={{ padding:0 }}><div className="table-wrapper" style={{ position:'relative', overflowX:'auto',background:'white',borderRadius:12 }}>
                                        <table className="entities-table tab-table w-100">
                                            <thead height="45px">
                                                <tr>
                                                    <th style={{ padding:'10px' }}>Platform</th>
                                                    <th style={{ padding:'10px' }}>Product Info</th>
                                                    <th style={{ padding:'10px' }}>SKU Info</th>
                                                    <th style={{ padding:'10px' }}>Platform Price</th>
                                                    <th style={{ padding:'10px' }}>Platform Stock</th>
                                                    <th style={{ padding:'10px' }}>Update Platform Stock</th>
                                                    <th style={{ padding:'10px', width:300, minWidth:300, maxWidth:300 }}>Notes</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {testResults.map((r, idx) => {
                                                    const isWooCommerce = r.platform?.toLowerCase().includes('woocommerce');
                                                    const isDisabled = r.skipped || r.hasError || isWooCommerce;
                                                    return (
                                                        <tr key={r.id || idx} className={idx % 2 === 1 ? 'striped' : ''}>
                                                            {/* Platform */}
                                                            <td>
                                                                {r.verified && !r.hasError ? <SuccessIcon /> : r.hasError ? <FailedIcon /> : null}
                                                                &nbsp;&nbsp;
                                                                {r.platform}
                                                            </td>
                                                            {/* Product Info */}
                                                            <td style={{ color: r.productError ? '#DC3545' : 'inherit' }}>
                                                                <div>{r.product_name}</div>
                                                                {!r.productError && r.product_id && (
                                                                    <div style={{ fontSize: 11, color: '#6C757D', marginTop: 2 }}>Product ID: {r.product_id}</div>
                                                                )}
                                                            </td>
                                                            {/* SKU Info */}
                                                            <td style={{ color: (r.skuError || r.productError) ? '#DC3545' : 'inherit' }}>
                                                                <div>{r.sku_name}</div>
                                                                {!r.skuError && !r.productError && r.sku_id && (
                                                                    <div style={{ fontSize: 11, color: '#6C757D', marginTop: 2 }}>SKU ID: {r.sku_id}</div>
                                                                )}
                                                            </td>
                                                            {/* Platform Price - a Lazada row also shows the Normal (list)
                                                                price Lazada returned, same line as Bulk Pricing (ERP-5162) */}
                                                            <td>
                                                                <div>{r.product_price}</div>
                                                                {r.normal_price != null && (
                                                                    <div style={{ fontSize: 11, color: '#6C757D', marginTop: 2 }}>
                                                                        Normal Price: {Number(r.normal_price).toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            {/* Platform Stock */}
                                                            <td>{r.prioduct_stock}</td>
                                                            {/* Update Platform Stock */}
                                                            <td>
                                                                <div className="aep-radio-group">
                                                                    {/* Sync ERP Qty */}
                                                                    <label
                                                                        className={isDisabled ? 'aep-radio-label-disabled' : 'aep-radio-label'}
                                                                        onClick={() => { if (!isDisabled) setStockUpdateOpts(p => ({ ...p, [r.id]: 'sync_erp' })); }}>
                                                                        {isDisabled ? (
                                                                            <span className="aep-radio-custom-disabled" />
                                                                        ) : stockUpdateOpts[r.id] === 'sync_erp' ? (
                                                                            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none" style={{ flexShrink: 0 }}>
                                                                                <g filter="url(#filter0_d_sync_erp)">
                                                                                    <rect x="4" y="3" width="20" height="20" rx="10" fill="#FFEB00"/>
                                                                                    <circle cx="14" cy="13" r="4" fill="black"/>
                                                                                </g>
                                                                                <defs>
                                                                                    <filter id="filter0_d_sync_erp" x="0" y="0" width="28" height="28" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                                                                        <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                                                                                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                                                                                        <feOffset dy="1"/>
                                                                                        <feGaussianBlur stdDeviation="2"/>
                                                                                        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"/>
                                                                                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow"/>
                                                                                        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape"/>
                                                                                    </filter>
                                                                                </defs>
                                                                            </svg>
                                                                        ) : (
                                                                            <span className="aep-radio-custom" />
                                                                        )}
                                                                        <span>Sync ERP Qty</span>
                                                                    </label>
                                                                    {/* Set to 0 */}
                                                                    <label
                                                                        className={isDisabled ? 'aep-radio-label-disabled' : 'aep-radio-label'}
                                                                        onClick={() => { if (!isDisabled) setStockUpdateOpts(p => ({ ...p, [r.id]: 'set_to_0' })); }}>
                                                                        {isDisabled ? (
                                                                            <span className="aep-radio-custom-disabled" />
                                                                        ) : stockUpdateOpts[r.id] === 'set_to_0' ? (
                                                                            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none" style={{ flexShrink: 0 }}>
                                                                                <g filter="url(#filter0_d_set_to_0)">
                                                                                    <rect x="4" y="3" width="20" height="20" rx="10" fill="#FFEB00"/>
                                                                                    <circle cx="14" cy="13" r="4" fill="black"/>
                                                                                </g>
                                                                                <defs>
                                                                                    <filter id="filter0_d_set_to_0" x="0" y="0" width="28" height="28" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                                                                        <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                                                                                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                                                                                        <feOffset dy="1"/>
                                                                                        <feGaussianBlur stdDeviation="2"/>
                                                                                        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"/>
                                                                                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow"/>
                                                                                        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape"/>
                                                                                    </filter>
                                                                                </defs>
                                                                            </svg>
                                                                        ) : (
                                                                            <span className="aep-radio-custom" />
                                                                        )}
                                                                        <span>Set to 0</span>
                                                                    </label>
                                                                </div>
                                                            </td>
                                                            {/* Notes — column fixed at 300px. A single-line input so a long
                                                                remark scrolls horizontally inside the box, like Brand ID,
                                                                instead of wrapping or widening the column. */}
                                                            <td style={{ width:300, minWidth:300, maxWidth:300 }}>
                                                                <input
                                                                    type="text"
                                                                    className="form-control common-input-all"
                                                                    value={mappingNotes[r.id] || ''}
                                                                    onChange={e => {
                                                                        // React 16 pools synthetic events: read the value now, because the
                                                                        // functional updater runs later, once e.target is already nulled.
                                                                        const value = e.target.value;
                                                                        setMappingNotes(pv => ({ ...pv, [r.id]: value }));
                                                                        setIsDirty(true);
                                                                    }}
                                                                    style={{ border: '1px solid #6C757D', color: '#212529', width: '100%' }}
                                                                />
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div></div></div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Discard modal */}
            {discardOpen && (
                <DiscardConfirmationModal
                    isOpen={discardOpen}
                    onCancel={() => { setDiscardOpen(false); setNextLocation(null); }}
                    onConfirm={confirmDiscard}
                    entityName="Platform Listing"
                />
            )}
        </div>
    );
}
